import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { Card } from "@/projects/components/ui/card";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Download, Printer, FileBarChart, Loader2 } from "lucide-react";
import { StatusBadge } from "@/projects/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/projects/components/ui/table";
import { useCallback, useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/components/ui/select";
import { toast } from "@/projects/lib/toast";
import { fetchTeachers } from "@/projects/lib/teachersApi";
import type { Teacher, Submission } from "@/projects/lib/store";
import type { ApiStudentVideoSubmission } from "@/projects/lib/submissionsApi";
import {
  REPORTS,
  exportReportCsv,
  fetchReport,
  getReportErrorMessage,
  type ReportKey,
} from "@/projects/lib/reportsApi";
import { cn } from "@/projects/lib/utils";

export function ReportsPage() {
  const [active, setActive] = useState<ReportKey>(REPORTS[0].key);
  const [teacherId, setTeacherId] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [rows, setRows] = useState<Submission[]>([]);
  const [rawRows, setRawRows] = useState<ApiStudentVideoSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchTeachers();
        if (!cancelled) {
          setTeachers(list);
        }
      } catch {
        if (!cancelled) {
          setTeachers([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchReport(active, {
        teacherUserId: teacherId,
        fromDate,
        toDate,
      });
      setRows(data.rows);
      setRawRows(data.raw);
    } catch (error) {
      toast.error(getReportErrorMessage(error, "Failed to load report data."));
      setRows([]);
      setRawRows([]);
    } finally {
      setLoading(false);
    }
  }, [active, teacherId, fromDate, toDate]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const activeLabel = REPORTS.find(r => r.key === active)?.label ?? "Report";

  const handleExport = async () => {
    if (!rawRows.length) {
      toast.error("No records to export.");
      return;
    }
    setExporting(true);
    try {
      // All API fields; video_attachment as hyperlink; grey header row.
      await exportReportCsv(rawRows, active);
      toast.success("Excel exported successfully");
    } catch {
      toast.error("Failed to export report.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <DashboardLayout
      role="admin"
      title="Reports"
      breadcrumb={["Home", "Reports"]}
    >
      <div className="grid gap-4 lg:grid-cols-4">
        <Card className="p-3 lg:col-span-1">
          <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Available Reports
          </div>
          <div className="mt-2 space-y-1">
            {REPORTS.map(r => (
              <button
                key={r.key}
                type="button"
                onClick={() => setActive(r.key)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm",
                  active === r.key
                    ? "bg-royal-soft text-royal"
                    : "hover:bg-muted"
                )}
              >
                <FileBarChart className="h-4 w-4 shrink-0" />
                {r.label}
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-4 lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-lg font-semibold">{activeLabel}</div>
              <div className="text-muted-foreground text-xs">
                {loading ? "Loading…" : `${rows.length} records`}
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-[10px] uppercase">
                  From
                </Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="h-9 w-[150px]"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-[10px] uppercase">
                  To
                </Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="h-9 w-[150px]"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-[10px] uppercase">
                  Teacher
                </Label>
                <Select value={teacherId} onValueChange={setTeacherId}>
                  <SelectTrigger className="h-9 w-[180px]">
                    <SelectValue placeholder="All Teachers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teachers</SelectItem>
                    {teachers.map(t => (
                      <SelectItem key={t.id} value={t.userId || t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => window.print()}
              >
                <Printer className="mr-2 h-4 w-4" /> Print
              </Button>
              <Button
                size="sm"
                className="h-9"
                disabled={exporting || loading || !rawRows.length}
                onClick={() => void handleExport()}
              >
                {exporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Export
              </Button>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Teacher</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-16 text-center">
                      <div className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading report…
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-muted-foreground py-10 text-center text-sm"
                    >
                      No records found.
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  rows.map(s => (
                    <TableRow key={`${s.apiId ?? s.id}-${s.submittedAt}`}>
                      <TableCell className="text-royal font-mono text-xs">
                        {s.id}
                      </TableCell>
                      <TableCell>{s.studentName}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {s.videoTitle}
                      </TableCell>
                      <TableCell>{s.assignedTeacherName ?? "—"}</TableCell>
                      <TableCell>
                        <StatusBadge status={s.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(s.submittedAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
